import { Lock } from 'lucide-react';
import { TaskStatusBadge } from '@/components/tasks/TaskBadges';
import { ALL_STATUSES, STATUS_META } from '@/components/tasks/taskConstants';

// The reviewable path (weekly / monthly / yearly / one-time). A daily task skips the
// review hop and goes straight from In Progress to Completed.
const FLOW: typeof ALL_STATUSES[number][] = ['pending_accept', 'in_progress', 'for_review', 'completed'];
const ALT_FLOW: typeof ALL_STATUSES[number][] = ['needs_improvement', 'discarded'];

export function TaskStatusTab() {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground">
          Task statuses are predefined and locked. They cannot be edited, deleted, or reordered. Each assignee has independent status tracking.
        </span>
      </div>

      <div>
        <h2 className="text-lg font-semibold">Predefined Task Statuses</h2>
        <p className="text-sm text-muted-foreground">These statuses are used to track task progress for each assignee.</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <table className="w-full text-sm">
          <thead className="bg-primary text-xs uppercase tracking-wider text-primary-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">#</th>
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-left font-semibold">Description</th>
              <th className="px-4 py-2.5 text-right font-semibold">Color</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {ALL_STATUSES.map((s, i) => (
              <tr key={s} className="hover:bg-muted/30">
                <td className="px-4 py-3 text-muted-foreground">{i + 1}</td>
                <td className="px-4 py-3"><TaskStatusBadge status={s} /></td>
                <td className="px-4 py-3 text-muted-foreground">{STATUS_META[s].description}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <span className="h-4 w-4 rounded-full" style={{ backgroundColor: STATUS_META[s].hex }} />
                    <span className="font-mono text-xs text-muted-foreground">{STATUS_META[s].hex}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Status Flow</h3>
        <div className="flex flex-wrap items-center gap-2">
          {FLOW.map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <TaskStatusBadge status={s} />
              {i < FLOW.length - 1 && <span className="text-muted-foreground">→</span>}
            </span>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Alternative flows:</span>
          {ALT_FLOW.map((s) => <TaskStatusBadge key={s} status={s} />)}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Daily tasks have no review step — Complete finishes them outright. On every other
          type the assignee submits for review, and the task creator either approves it or
          returns it as Need Improvement, which sends it back to be submitted again.
        </p>
      </div>
    </div>
  );
}
